import { useCallback, useRef, useState } from 'react';
import { useAppStore } from '@/state/store';

/**
 * A companion file (e.g. a rougher-axis turning program) that sometimes has
 * data in a region the main .C export skipped — used only to fill detected
 * gaps, never to override real .C data. Accepts any extension since these
 * come in several forms (.V, .U, .TUR, .SAN) but share the same
 * Position;Radius row format.
 */
export function DetailFileUpload() {
  const loadDetailFile = useAppStore((s) => s.loadDetailFile);
  const clearDetailFile = useAppStore((s) => s.clearDetailFile);
  const detailFileName = useAppStore((s) => s.detailFileName);
  const model = useAppStore((s) => s.model);
  const unresolvedGapCount = model ? model.trim.gaps.filter((g) => !g.filled).length : 0;
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      void loadDetailFile(files[0]);
    },
    [loadDetailFile],
  );

  if (!model) return null;

  return (
    <div>
      <div className="sidebar-section-label">
        Detail File (optional)
        {unresolvedGapCount > 0 && (
          <span style={{ color: 'var(--danger)', marginLeft: 6 }}>
            · {unresolvedGapCount} gap{unresolvedGapCount === 1 ? '' : 's'} detected
          </span>
        )}
      </div>

      {detailFileName ? (
        <div className="stat-row" style={{ borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
          <span className="stat-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detailFileName}
          </span>
          <button className="btn btn-ghost" style={{ width: 'auto', padding: '3px 8px' }} onClick={clearDetailFile}>
            Remove
          </button>
        </div>
      ) : (
        <div
          className={`dropzone${isDragging ? ' is-dragging' : ''}`}
          style={{ padding: '14px 10px' }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          role="button"
          tabIndex={0}
          aria-label="Upload an optional companion detail file to fill detected gaps"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
        >
          <div className="dropzone-title" style={{ fontSize: 11.5 }}>
            Drop a companion file (.V, .U, .TUR, .SAN)
          </div>
          <div className="dropzone-subtitle">used only to fill detected gaps</div>
          <input ref={inputRef} type="file" onChange={(e) => handleFiles(e.target.files)} />
        </div>
      )}
    </div>
  );
}
